import { useCallback, useState } from 'react';
import { UseFileSelectionProps, UseFileSelectionReturn } from './FileUploader.types';
import {
  KNOWLEDGE_BASE_ALLOWED_EXTENSIONS,
  FILE_VALIDATION_MESSAGES,
} from '@/shared/constants/file-upload.constants';
import { FILE_SIZE_LIMITS } from '@inference.engine/file-utils/web';

function fileExtension(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.slice(idx).toLowerCase() : '';
}

export const useFileSelection = ({
  onAddDocuments,
  allowedExtensions = KNOWLEDGE_BASE_ALLOWED_EXTENSIONS,
}: UseFileSelectionProps): UseFileSelectionReturn => {
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > FILE_SIZE_LIMITS.ASSETS.DEFAULT) {
        return FILE_VALIDATION_MESSAGES.SIZE_EXCEEDED(
          file.name,
          Math.round(FILE_SIZE_LIMITS.ASSETS.DEFAULT / (1024 * 1024)),
        );
      }

      if (allowedExtensions.length > 0 && !allowedExtensions.includes(fileExtension(file.name))) {
        return FILE_VALIDATION_MESSAGES.UNSUPPORTED_TYPE(file.name);
      }

      return null;
    },
    [allowedExtensions],
  );

  const handleDrop = useCallback(
    (newFiles: File[]) => {
      const validationErrors: string[] = [];
      const validFiles: File[] = [];

      newFiles.forEach((file) => {
        const error = validateFile(file);
        if (error) {
          validationErrors.push(error);
        } else {
          validFiles.push(file);
        }
      });

      setErrors(validationErrors);

      if (validFiles.length > 0) {
        setFiles((prev) => [...prev, ...validFiles]);
      }
    },
    [validateFile],
  );

  const clearFiles = useCallback(() => {
    setFiles([]);
    setErrors([]);
  }, []);

  const handleAddDocuments = useCallback(async () => {
    if (files.length > 0 && errors.length === 0) {
      try {
        await onAddDocuments(files);
      } catch {
        setErrors([FILE_VALIDATION_MESSAGES.UPLOAD_FAILED]);
      }
    }
  }, [files, errors, onAddDocuments]);

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return {
    files,
    errors,
    handleDrop,
    handleAddDocuments,
    clearFiles,
    removeFile,
  };
};
